/**
 * Color Picker for Shopify Storefront - CDN Version
 * Vanilla JS - No dependencies - Optimized for 1M+ colors
 * 
 */

(function() {
	'use strict';

	/**
	 * CONFIGURATION
	 * Replace with your config.json URL from WordPress JSON Generator
	 */
	const DEFAULT_CONFIG = {
		CDN_CONFIG_URL: 'https://nl-colorpicker.ankerstuy.com/wp-content/uploads/color-catalog-json/config.json',
		MAX_RESULTS: 40,
		DEBOUNCE_DELAY: 300,
		CACHE_DURATION: 3600000 // 1 hour in milliseconds
	};

	// Allow window.CCM_CONFIG to override defaults (for testing)
	const CONFIG = window.CCM_CONFIG ? Object.assign({}, DEFAULT_CONFIG, window.CCM_CONFIG) : DEFAULT_CONFIG;

	/**
	 * Color Picker Class - CDN Version
	 * Loads categories/colors from static JSON files
	 * Only uses REST API for search
	 */
	class ColorPickerCDN {
		constructor(config) {
			this.config = config;
			this.cdnConfig = null; // Loaded from config.json
			this.modal = null;
			this.searchInput = null;
			this.searchScopeRadios = null;
			this.categorySelect = null;
			this.resultsContainer = null;
			this.currentRequest = null;
			this.debounceTimer = null;
			this.cache = new Map();
			this.selectedColor = null;
			this.productForm = null;
			this.categories = [];
			this.popularCategoryId = null; // Store popular category ID
			this.colorButton = null; // Store reference to color button
			this.variantExplicitlySelected = false; // Track if user has explicitly selected a variant
			// Shopify product ID – used to load product-specific color category restrictions
			this.productId = window.productData ? String(window.productData.id) : null;
			
			// Pagination state
			this.currentColors = []; // Store all loaded colors
			this.displayedCount = 0; // Track how many are currently displayed
			this.colorsPerPage = 40; // How many to show per load
			this.nextPageUrl = null; // URL to next page of colors (for lazy loading)
			this.totalCount = 0; // Total count of colors in category/subcategory

			this.init();
		}

		/**
		 * Initialize color picker
		 */
		init() {
			// Wait for DOM to be ready
			if (document.readyState === 'loading') {
				document.addEventListener('DOMContentLoaded', () => this.setup());
			} else {
				this.setup();
			}
		}

		/**
		 * Setup color picker on product page
		 */
		async setup() {
			// Only run on product pages - match Shopify form structure
			this.productForm = document.querySelector('form.form[data-type="add-to-cart-form"]');
			
			if (!this.productForm) {
				return; // Not a product page
			}

			// Inject "Choose Color" button first (but keep it hidden)
			this.injectButton();

			// Setup form validation immediately (validation is conditional based on variant)
			this.setupFormValidation();

			// Don't load CDN config or create modal until button is actually clicked
			// This prevents unnecessary loading on page load
			// Button will remain hidden until user explicitly selects a variant
		}

		/**
		 * Load CDN configuration from config.json
		 */
		async loadCDNConfig() {
			const cached = this.getCache('cdn_config');
			if (cached) {
				this.cdnConfig = cached;
				return;
			}

			const response = await fetch(this.config.CDN_CONFIG_URL);
			if (!response.ok) {
				throw new Error('Failed to load CDN config');
			}

			this.cdnConfig = await response.json();
			this.setCache('cdn_config', this.cdnConfig);
		}

		/**
		 * Inject "Choose Color" button into product form
		 * Position: Above buy buttons, below variant picker
		 */
		injectButton() {
			// Find the product-form wrapper (contains the form with buy buttons)
			const productFormWrapper = this.productForm.closest('product-form');
			
			if (!productFormWrapper) {
				return;
			}

			const colorButton = document.createElement('button');
			colorButton.type = 'button';
			colorButton.className = 'ccm-choose-color-btn button';
			colorButton.textContent = 'Kies je kleur';
			
			colorButton.addEventListener('click', async (e) => {
				e.preventDefault();
				// Load CDN config and create modal only when button is clicked
				if (!this.cdnConfig) {
					try {
						await this.loadCDNConfig();
					} catch (error) {
						console.error('ColorPicker: Failed to load CDN config', error);
						return;
					}
				}
				
				if (!this.modal) {
					this.createModal();
				}
				
				this.openModal();
			});

			// Insert before the product-form wrapper (which contains buy buttons)
			// This places it after variant picker and before buy buttons
			productFormWrapper.parentNode.insertBefore(colorButton, productFormWrapper);

			// Store reference to button
			this.colorButton = colorButton;

			// Initially hide the button (will be shown if variant supports it)
			this.colorButton.style.display = 'none';

			// Listen for variant changes
			this.setupVariantChangeListener();

			// Check initial variant state after a short delay to ensure variant input is populated
			setTimeout(() => {
				this.updateButtonVisibility();
			}, 100);
		}

		/**
		 * Setup listener for variant selection changes
		 */
		setupVariantChangeListener() {
			// Listen for changes to the variant ID input field
			const variantInput = this.productForm.querySelector('input[name="id"]');
			if (variantInput) {
				variantInput.addEventListener('change', () => {
					this.variantExplicitlySelected = true;
					this.updateButtonVisibility();
				});
			}

			// Also listen for form changes (for variant pickers that don't directly change the input)
			this.productForm.addEventListener('change', (e) => {
				// Check if any variant-related element changed
				if (e.target.matches('input[name="id"], select[name="id"], input[type="radio"][name^="option"]')) {
					this.variantExplicitlySelected = true;
					// Use setTimeout to ensure variant ID is updated
					setTimeout(() => {
						this.updateButtonVisibility();
					}, 10);
				}
			});

			// Listen for custom Shopify variant change events if they exist
			document.addEventListener('variant:change', () => {
				this.variantExplicitlySelected = true;
				this.updateButtonVisibility();
			});

			// Listen for product form updates (some themes use this)
			const productFormElement = this.productForm.closest('product-form');
			if (productFormElement) {
				productFormElement.addEventListener('change', () => {
					this.variantExplicitlySelected = true;
					setTimeout(() => {
						this.updateButtonVisibility();
					}, 10);
				});
			}
		}

		/**
		 * Check if current variant has color_picker enabled
		 */
		hasColorPickerVariant() {
			if (!window.productData) {
				return false;
			}

			const variantInput = this.productForm.querySelector('input[name="id"]');
			if (!variantInput) {
				return false;
			}

			const selectedVariantId = parseInt(variantInput.value, 10);
			if (!selectedVariantId) {
				return false;
			}

			// Find the variant in productData
			const variant = window.productData.variants.find(v => v.id === selectedVariantId);
			return variant && variant.color_picker === true;
		}

		/**
		 * Update button visibility based on selected variant's color_picker property
		 */
	updateButtonVisibility() {
		if (!this.colorButton) {
			return;
		}

		// Show button if current variant supports color_picker
		// This works for both initial page load (default variant) and explicit selection
		if (this.hasColorPickerVariant()) {
			this.colorButton.style.display = '';
			// Show success message if color already selected
			const selectedField = this.productForm.querySelector('input[name="properties[Colour]"]');
			if (selectedField && selectedField.value) {
				this.showSuccessMessage(null, true);
			}
		} else {
			this.colorButton.style.display = 'none';
			// Remove color properties when switching to non-colored variant
			this.removeColorProperties();
			// Also hide success message if button is hidden
			const successMessage = document.querySelector('.ccm-success-message');
			if (successMessage) {
				successMessage.remove();
			}
		}
	}

		/**
		 * Create modal HTML structure
		 */
		createModal() {
			const modal = document.createElement('div');
			modal.className = 'ccm-modal';
			modal.id = 'ccm-color-modal';
			
			modal.innerHTML = `
				<div class="ccm-modal-overlay"></div>
				<div class="ccm-modal-content">
					<div class="ccm-modal-header">
						<h2>Kies de perfecte kleur</h2>
						<button type="button" class="ccm-modal-close" aria-label="Close">&times;</button>
					</div>
					<div class="ccm-modal-body">
					<div class="ccm-filter-section">
						<div class="ccm-filter-group-container">
							<div class="ccm-filter-group">
								<label for="ccm-search-input">Zoeken op naam of kleurcode</label>
								<div class="ccm-search-input-wrapper">
									<input 
										type="text" 
										id="ccm-search-input" 
										class="ccm-search-input" 
										placeholder="Naam of kleurcode"
										autocomplete="off"
									/>
									<button type="button" class="ccm-search-btn" id="ccm-search-btn" aria-label="Search">Search</button>
								</div>
								<div class="ccm-search-scope">
									<label class="ccm-radio-label">
										<input 
											type="radio" 
											name="search-scope" 
											value="all" 
											class="ccm-radio-input"
											checked="checked"
										/>
										<span>Entire database</span>
									</label>							
									<label class="ccm-radio-label">
										<input 
											type="radio" 
											name="search-scope" 
											value="current" 
											class="ccm-radio-input"
										/>
										<span>within colour chart</span>
									</label>
								</div>
							</div>
							<div class="ccm-filter-group">
								<label for="ccm-category-select">Kies via een kleurenwaaier:</label>
								<select id="ccm-category-select" class="ccm-filter-select">
									<option value="">-- Kies een kleurenwaaler</option>
								</select>
								<div class="ccm-category-text">Not sure what to choose? <a href="https://www.ankerstuyshop.co.uk/pages/contact" target="_blank">Get in touch with us</a></div>
							</div>
							<div class="ccm-filter-notice">
								<p>Let op! De kleuren op het beeldscherm dienen alleen ter ondersteuning en kunnen afwijken van het echte product.</p>
							</div>
						</div>
					</div>
					<div class="ccm-custom-color-notice">
						<p>Can't find your colour or brand? <button type="button" class="ccm-custom-color-link" id="ccm-custom-color-btn">Click here to enter your desired colour</button></p>
					</div>
					<div class="ccm-custom-color-form" id="ccm-custom-color-form" style="display: none;">
						<h3>Enter Custom colour</h3>
						<div class="ccm-form-group">
							<label for="ccm-custom-name">Colour Brand *</label>
							<input type="text" id="ccm-custom-name" class="ccm-form-input" placeholder="e.g RAL, NCS" required />
						</div>
						<div class="ccm-form-group">
							<label for="ccm-custom-code">Colour Name or Code *</label>
							<input type="text" id="ccm-custom-code" class="ccm-form-input" placeholder="e.g RAL 9016, NCS S 0550-N" required />
						</div>
						<!--
						<div class="ccm-form-group">
							<label for="ccm-custom-rgb">RGB Values (optional)</label>
							<div class="ccm-rgb-inputs">
								<input type="number" id="ccm-custom-rgb-r" class="ccm-form-input-small" placeholder="R" min="0" max="255" />
								<input type="number" id="ccm-custom-rgb-g" class="ccm-form-input-small" placeholder="G" min="0" max="255" />
								<input type="number" id="ccm-custom-rgb-b" class="ccm-form-input-small" placeholder="B" min="0" max="255" />
							</div>
						</div>
						-->
						<div class="ccm-form-actions">
							<button type="button" class="ccm-btn-cancel" id="ccm-custom-cancel">Cancel</button>
							<button type="button" class="ccm-btn-submit" id="ccm-custom-submit">Select This Colour</button>
						</div>
					</div>
					<div class="ccm-results-container" id="ccm-results">
						<p class="ccm-help-text">Search or select a category to browse colours.</p>
					</div>
					</div>
				</div>
			`;

			document.body.appendChild(modal);

		// Store references
		this.modal = modal;
		this.searchInput = modal.querySelector('#ccm-search-input');
		this.searchButton = modal.querySelector('#ccm-search-btn');
		this.searchScopeRadios = modal.querySelectorAll('input[name="search-scope"]');
		this.categorySelect = modal.querySelector('#ccm-category-select');
		this.resultsContainer = modal.querySelector('#ccm-results');

			// Setup event listeners
			this.setupEventListeners();
		}

		/**
		 * Setup modal event listeners
		 */
		setupEventListeners() {
			// Close button
			const closeBtn = this.modal.querySelector('.ccm-modal-close');
			closeBtn.addEventListener('click', () => this.closeModal());

			// Overlay click
			const overlay = this.modal.querySelector('.ccm-modal-overlay');
			overlay.addEventListener('click', () => this.closeModal());

			// ESC key
			document.addEventListener('keydown', (e) => {
				if (e.key === 'Escape' && this.modal.style.display === 'flex') {
					this.closeModal();
				}
			});

		// Search button click - manual search trigger
		this.searchButton.addEventListener('click', () => {
			this.performManualSearch();
		});

		// Allow Enter key to trigger search for better UX
		this.searchInput.addEventListener('keydown', (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				this.performManualSearch();
			}
		});

		// Search scope radio buttons - no longer auto-trigger search
		this.searchScopeRadios.forEach(radio => {
			radio.addEventListener('change', () => {
				// Scope change doesn't trigger search anymore
				// User needs to click search button again
			});
		});

	// Category/Subcategory combined select
	this.categorySelect.addEventListener('change', (e) => {
		this.handleSelectionChange(e.target.value);
	});

		// Custom color button
		const customColorBtn = this.modal.querySelector('#ccm-custom-color-btn');
		customColorBtn.addEventListener('click', () => this.showCustomColorForm());

		// Custom color form cancel
		const customCancelBtn = this.modal.querySelector('#ccm-custom-cancel');
		customCancelBtn.addEventListener('click', () => this.hideCustomColorForm());

		// Custom color form submit
		const customSubmitBtn = this.modal.querySelector('#ccm-custom-submit');
		customSubmitBtn.addEventListener('click', () => this.submitCustomColor());
	}

	/**
	 * Open modal
	 */
	async openModal() {
		// Check if current variant has color_picker enabled
		if (!this.hasColorPickerVariant()) {
			return; // Don't open modal if variant doesn't support color picker
		}

		// Ensure modal exists
		if (!this.modal) {
			return;
		}

		this.modal.style.display = 'flex';
		//this.searchInput.focus();
		document.body.style.overflow = 'hidden';

		// Load categories if not loaded
		if (this.categories.length === 0) {
			await this.loadCategories();
		} else {
			// Categories already cached — apply the same popular/first-category fallback logic
			const popularAvailable = this.popularCategoryId &&
				this.categories.some(cat => parseInt(cat.id, 10) === parseInt(this.popularCategoryId, 10));
			if (popularAvailable) {
				await this.loadPopularCategory();
			} else if (this.categories.length > 0) {
				const firstCat = this.categories[0];
				this.categorySelect.value = `cat_${firstCat.id}`;
				await this.loadColorsByCategory(firstCat.id);
			}
		}
	}

		/**
		 * Close modal
		 */
		closeModal() {
			this.modal.style.display = 'none';
			this.searchInput.value = '';
			this.categorySelect.value = '';
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Search or select a category to browse colours.</p>';
			document.body.style.overflow = '';

			// Abort any pending request
			if (this.currentRequest) {
				this.currentRequest.abort();
				this.currentRequest = null;
			}
		}

	/**
	 * Show custom color entry form
	 */
	showCustomColorForm() {
		const form = this.modal.querySelector('#ccm-custom-color-form');
		const results = this.resultsContainer;
		
		// Hide results and show form
		results.style.display = 'none';
		form.style.display = 'block';
		
		// Clear form inputs
		this.modal.querySelector('#ccm-custom-name').value = '';
		this.modal.querySelector('#ccm-custom-code').value = '';
		const rgbRInput = this.modal.querySelector('#ccm-custom-rgb-r');
		const rgbGInput = this.modal.querySelector('#ccm-custom-rgb-g');
		const rgbBInput = this.modal.querySelector('#ccm-custom-rgb-b');
		if (rgbRInput) rgbRInput.value = '';
		if (rgbGInput) rgbGInput.value = '';
		if (rgbBInput) rgbBInput.value = '';
		
		// Focus on name input
		this.modal.querySelector('#ccm-custom-name').focus();
	}

	/**
	 * Hide custom color entry form
	 */
	hideCustomColorForm() {
		const form = this.modal.querySelector('#ccm-custom-color-form');
		const results = this.resultsContainer;
		
		// Show results and hide form
		form.style.display = 'none';
		results.style.display = 'block';
	}

	/**
	 * Submit custom color
	 */
	submitCustomColor() {
		const nameInput = this.modal.querySelector('#ccm-custom-name');
		const codeInput = this.modal.querySelector('#ccm-custom-code');
		const rgbRInput = this.modal.querySelector('#ccm-custom-rgb-r');
		const rgbGInput = this.modal.querySelector('#ccm-custom-rgb-g');
		const rgbBInput = this.modal.querySelector('#ccm-custom-rgb-b');
		
		// Validate required fields
		const name = nameInput.value.trim();
		const code = codeInput.value.trim();
		
		if (!name || !code) {
			alert('Please enter both Colour Name and Colour Code.');
			return;
		}
		
		// Get RGB values (optional) - check if inputs exist first
		const r = rgbRInput && rgbRInput.value ? parseInt(rgbRInput.value) : null;
		const g = rgbGInput && rgbGInput.value ? parseInt(rgbGInput.value) : null;
		const b = rgbBInput && rgbBInput.value ? parseInt(rgbBInput.value) : null;
		
		// Validate RGB if provided
		if ((r !== null || g !== null || b !== null) && (r === null || g === null || b === null)) {
			alert('Please enter all three RGB values (R, G, and B) or leave them all empty.');
			return;
		}
		
		// Create custom color object
		const customColor = {
			id: 'custom',
			name: name,
			code: code,
			rgb: r !== null && g !== null && b !== null ? { r, g, b } : { r: 128, g: 128, b: 128 },
			rgb_string: r !== null && g !== null && b !== null ? `rgb(${r},${g},${b})` : 'rgb(128,128,128)'
		};
		
		// Select the custom color
		this.selectColor(customColor);
	}

	/**
	 * Load categories with nested subcategories.
	 * First performs a cheap gate check to see if the product has any category
	 * restrictions. If not, loads from the static CDN file. Only calls the full
	 * /product-categories endpoint when restrictions actually exist.
	 */
	async loadCategories() {
		try {
			if (this.productId && this.cdnConfig && this.cdnConfig.api_base_url) {
				// Gate check: cheaply determine whether this product has any
				// category restrictions before calling the expensive full endpoint.
				const gateKey = `product_has_restrictions_${this.productId}`;
				let hasRestrictions = this.getCache(gateKey);

				if (hasRestrictions === null) {
					// Not cached yet – ask the server
					const gateUrl = `${this.cdnConfig.api_base_url}/product-has-restrictions?product_id=${encodeURIComponent(this.productId)}`;
					const gateResponse = await fetch(gateUrl);
					if (!gateResponse.ok) throw new Error('Failed to check product restrictions');
					const gateResult = await gateResponse.json();
					hasRestrictions = (gateResult.success && gateResult.data)
						? gateResult.data.has_restrictions
						: false;
					this.setCache(gateKey, hasRestrictions);
				}

				if (hasRestrictions) {
					// Product has custom restrictions – fetch full filtered category tree
					const url = `${this.cdnConfig.api_base_url}/product-categories?product_id=${encodeURIComponent(this.productId)}`;
					const response = await fetch(url);
					if (!response.ok) throw new Error('Failed to load product categories');
					const result = await response.json();
					if (result.success && result.data) {
						this.categories = result.data.categories || [];
						this.popularCategoryId = result.data.popular_category_id || null;
					}
				} else {
					// No restrictions – use static CDN file
					const cached = this.getCache('categories_with_subcategories');
					let data;

					if (cached) {
						data = cached;
					} else {
						const url = this.cdnConfig.endpoints.categories;
						const response = await fetch(url);
						if (!response.ok) throw new Error('Failed to load categories');
						data = await response.json();
						this.setCache('categories_with_subcategories', data);
					}

					this.categories = data.categories || [];
					this.popularCategoryId = data.popular_category_id || null;
				}
			} else {
				// Default path: load from CDN static file
				const cached = this.getCache('categories_with_subcategories');
				let data;

				if (cached) {
					data = cached;
				} else {
					const url = this.cdnConfig.endpoints.categories;
					const response = await fetch(url);
					if (!response.ok) throw new Error('Failed to load categories');
					data = await response.json();
					this.setCache('categories_with_subcategories', data);
				}

				this.categories = data.categories || [];
				this.popularCategoryId = data.popular_category_id || null;
			}

			// Ensure each category has a subcategories array (even if empty)
			this.categories.forEach(cat => {
				if (!cat.subcategories) {
					cat.subcategories = [];
				}
			});

			// Populate the combined select
			this.populateCombinedSelect();

			// Load popular category if available, otherwise fall back to the first category in the list
			if (this.categories.length > 0) {
				const popularAvailable = this.popularCategoryId &&
					this.categories.some(cat => parseInt(cat.id, 10) === parseInt(this.popularCategoryId, 10));
				if (popularAvailable) {
					await this.loadPopularCategory();
				} else {
					const firstCat = this.categories[0];
					this.categorySelect.value = `cat_${firstCat.id}`;
					await this.loadColorsByCategory(firstCat.id);
				}
			}

		} catch (error) {
			console.error('Failed to load categories', error);
			this.showError('Failed to load categories');
		}
	}

	/**
	 * Populate combined category/subcategory dropdown
	 */
	populateCombinedSelect() {
		this.categorySelect.innerHTML = '<option value="">-- Kies een kleurenwaaler</option>';
		
		this.categories.forEach(cat => {
			// Add category as regular option
			const catOption = document.createElement('option');
			catOption.value = `cat_${cat.id}`;
			catOption.textContent = cat.name;
			catOption.className = 'ccm-category-option';
			this.categorySelect.appendChild(catOption);

			// Add subcategories with indentation
			if (cat.subcategories && cat.subcategories.length > 0) {
				cat.subcategories.forEach(subcat => {
					const subcatOption = document.createElement('option');
					subcatOption.value = `subcat_${subcat.id}`;
					subcatOption.textContent = `\u00A0\u00A0\u00A0\u00A0${subcat.name}`; // Add spaces for indentation
					subcatOption.className = 'ccm-subcategory-option';
					this.categorySelect.appendChild(subcatOption);
				});
			}
		});
	}

	/**
	 * Load popular category colors automatically
	 */
	async loadPopularCategory() {
		// Set the select to popular category
		this.categorySelect.value = `cat_${this.popularCategoryId}`;
		
		// Load colors from popular category
		await this.loadColorsByCategory(this.popularCategoryId);
	}

	/**
	 * Handle selection change (category or subcategory)
	 */
	async handleSelectionChange(value) {
		// Clear search input
		this.searchInput.value = '';

		if (!value) {
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Search or select a category to browse colours.</p>';
			return;
		}

		// Check if it's a category or subcategory
		if (value.startsWith('cat_')) {
			const categoryId = value.replace('cat_', '');
			await this.loadColorsByCategory(categoryId);
		} else if (value.startsWith('subcat_')) {
			const subcategoryId = value.replace('subcat_', '');
			await this.loadColorsBySubcategory(subcategoryId);
		}
	}

		/**
		 * Load colors by category from CDN (with lazy loading pagination)
		 */
		async loadColorsByCategory(categoryId) {
			this.showLoading();

			try {
				// Reset pagination state
				this.currentColors = [];
				this.displayedCount = 0;
				this.nextPageUrl = null;
				this.totalCount = 0;

				// Load first page only
				const baseUrl = this.cdnConfig.endpoints.colors.by_category.replace('{category_id}', categoryId);
				const firstPageUrl = baseUrl.replace('.json', '-page-1.json');
				
				try {
					// Try paginated version first
					const response = await fetch(firstPageUrl);
					if (response.ok) {
						// Paginated - load first page only
						const data = await response.json();
						this.currentColors = data.colors || [];
						this.totalCount = data.total_count || this.currentColors.length;
						
						// Store next page URL if it exists
						if (data.next) {
							const currentUrlObj = new URL(firstPageUrl);
							const baseUrlPath = currentUrlObj.origin + currentUrlObj.pathname.substring(0, currentUrlObj.pathname.lastIndexOf('/') + 1);
							this.nextPageUrl = baseUrlPath + data.next;
						}
					} else {
						// Fall back to non-paginated
						const fallbackResponse = await fetch(baseUrl);
						if (!fallbackResponse.ok) throw new Error('Failed to load colors');
						const data = await fallbackResponse.json();
						this.currentColors = data.colors || [];
						this.totalCount = data.count || this.currentColors.length;
						this.nextPageUrl = null; // No pagination
					}
				} catch (error) {
					// If page-1 fails, try non-paginated
					const fallbackResponse = await fetch(baseUrl);
					if (!fallbackResponse.ok) throw new Error('Failed to load colors');
					const data = await fallbackResponse.json();
					this.currentColors = data.colors || [];
					this.totalCount = data.count || this.currentColors.length;
					this.nextPageUrl = null; // No pagination
				}

				// Render first batch of results
				this.renderResults(this.currentColors, false);

			} catch (error) {
				console.error('Failed to load colors', error);
				this.showError('Failed to load colors');
			}
		}

		/**
		 * Load colors by subcategory from CDN (with lazy loading pagination)
		 */
		async loadColorsBySubcategory(subcategoryId) {
			this.showLoading();

			try {
				// Reset pagination state
				this.currentColors = [];
				this.displayedCount = 0;
				this.nextPageUrl = null;
				this.totalCount = 0;

				// Load first page only
				const baseUrl = this.cdnConfig.endpoints.colors.by_subcategory.replace('{subcategory_id}', subcategoryId);
				const firstPageUrl = baseUrl.replace('.json', '-page-1.json');
				
				try {
					// Try paginated version first
					const response = await fetch(firstPageUrl);
					if (response.ok) {
						// Paginated - load first page only
						const data = await response.json();
						this.currentColors = data.colors || [];
						this.totalCount = data.total_count || this.currentColors.length;
						
						// Store next page URL if it exists
						if (data.next) {
							const currentUrlObj = new URL(firstPageUrl);
							const baseUrlPath = currentUrlObj.origin + currentUrlObj.pathname.substring(0, currentUrlObj.pathname.lastIndexOf('/') + 1);
							this.nextPageUrl = baseUrlPath + data.next;
						}
					} else {
						// Fall back to non-paginated
						const fallbackResponse = await fetch(baseUrl);
						if (!fallbackResponse.ok) throw new Error('Failed to load colors');
						const data = await fallbackResponse.json();
						this.currentColors = data.colors || [];
						this.nextPageUrl = null; // No pagination
					}
				} catch (error) {
					// If page-1 fails, try non-paginated
					const fallbackResponse = await fetch(baseUrl);
					if (!fallbackResponse.ok) throw new Error('Failed to load colors');
					const data = await fallbackResponse.json();
					this.currentColors = data.colors || [];
					this.nextPageUrl = null; // No pagination
				}

				// Render first batch of results
				this.renderResults(this.currentColors, false);

			} catch (error) {
				console.error('Failed to load colors', error);
				this.showError('Failed to load colors');
			}
		}

	/**
	 * Perform manual search when button is clicked
	 */
	performManualSearch() {
		const query = this.searchInput.value.trim();
		
		if (query.length === 0) {
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Search or select a category to browse colours.</p>';
			return;
		}

		// Minimum 3 characters required
		if (query.length < 3) {
			this.resultsContainer.innerHTML = '<p class="ccm-help-text">Please enter at least 3 characters to search.</p>';
			return;
		}

		// Show loading immediately
		this.showLoading();

		// Perform search directly
		this.searchColors(query);
	}

	/**
	 * Search colors via REST API
	 * Note: Search still uses REST API for dynamic querying
	 */
	async searchColors(query) {
		// Reset pagination state for search
		this.currentColors = [];
		this.displayedCount = 0;
		this.nextPageUrl = null;
		this.totalCount = 0;

		// Get search scope
		const searchScope = document.querySelector('input[name="search-scope"]:checked')?.value || 'all';
		
		// If searching within current selection, check if category is selected
		if (searchScope === 'current') {
			if (!this.categorySelect.value) {
				this.resultsContainer.innerHTML = '<p class="ccm-help-text">Please select a category or subcategory first, then search within it.</p>';
				return;
			}
		}
		
		// Abort previous request
		if (this.currentRequest) {
			this.currentRequest.abort();
		}

		try {
			this.currentRequest = new AbortController();
			
			// Build URL with search scope parameters
			// Use a much higher limit for search to allow pagination (9999 = effectively unlimited)
			let url = `${this.cdnConfig.api_base_url}/search?q=${encodeURIComponent(query)}&limit=9999`;

			// Attach product ID so the server restricts search to allowed categories
			if (this.productId) {
				url += `&product_id=${encodeURIComponent(this.productId)}`;
			}

			// Add category/subcategory filter if searching within current selection
			if (searchScope === 'current' && this.categorySelect.value) {
				const selectedValue = this.categorySelect.value;
				
				if (selectedValue.startsWith('cat_')) {
					const categoryId = selectedValue.replace('cat_', '');
					url += `&category_id=${categoryId}`;
				} else if (selectedValue.startsWith('subcat_')) {
					const subcategoryId = selectedValue.replace('subcat_', '');
					url += `&subcategory_id=${subcategoryId}`;
				}
			}
			
			const response = await fetch(url, {
				signal: this.currentRequest.signal
			});

			const result = await response.json();

			if (!response.ok) {
				// Handle API error response
				const errorMessage = result.error || 'Search failed. Please try again.';
				this.showError(errorMessage);
				this.currentRequest = null;
				return;
			}
			
			if (result.success && result.data && result.data.colors) {
				// Update state with search results
				this.currentColors = result.data.colors;
				this.totalCount = result.data.colors.length; // For search, total is what we got
				this.displayedCount = 0;
				this.nextPageUrl = null; // No pagination for search results
				
				// Render results
				this.renderResults(this.currentColors, false);
			} else {
				this.showError('No colours found');
			}

			this.currentRequest = null;

		} catch (error) {
			if (error.name === 'AbortError') {
				// Request was cancelled, ignore
				return;
			}
			console.error('Search error', error);
			this.showError('Search failed. Please try again.');
		}
	}

		/**
		 * Render color results with pagination support
		 * @param {Array} colors - Array of colors to display
		 * @param {boolean} append - If true, append to existing results
		 */
		renderResults(colors, append = false) {
			if (!colors || colors.length === 0) {
				this.resultsContainer.innerHTML = '<p class="ccm-no-results">No colours found.</p>';
				return;
			}

			// Store colors for pagination
			if (!append) {
				this.currentColors = colors;
				this.displayedCount = 0;
			}

			// Calculate how many colors to display
			const startIndex = this.displayedCount;
			const endIndex = Math.min(startIndex + this.colorsPerPage, this.currentColors.length);
			const colorsToShow = this.currentColors.slice(startIndex, endIndex);

			// Create or get existing grid
			let grid;
			if (append) {
				grid = this.resultsContainer.querySelector('.ccm-color-grid');
				if (!grid) {
					grid = document.createElement('div');
					grid.className = 'ccm-color-grid';
					this.resultsContainer.appendChild(grid);
				}
			} else {
				this.resultsContainer.innerHTML = '';
				grid = document.createElement('div');
				grid.className = 'ccm-color-grid';
				this.resultsContainer.appendChild(grid);
			}

			// Render colors
			colorsToShow.forEach(color => {
				const card = this.createColorCard(color);
				grid.appendChild(card);
			});

			// Update displayed count
			this.displayedCount = endIndex;

			// Add or update "Load More" button
			this.updateLoadMoreButton();
		}

		/**
		 * Update or create "Load More" button
		 */
		updateLoadMoreButton() {
			// Remove existing button or message
			const existingBtn = this.resultsContainer.querySelector('.ccm-load-more');
			const existingMsg = this.resultsContainer.querySelector('.ccm-all-loaded');
			if (existingBtn) existingBtn.remove();
			if (existingMsg) existingMsg.remove();

			// Calculate total remaining (not yet displayed)
			const totalRemaining = this.totalCount - this.displayedCount;

			if (totalRemaining > 0) {
				const loadMoreBtn = document.createElement('button');
				loadMoreBtn.type = 'button';
				loadMoreBtn.className = 'ccm-load-more';
				loadMoreBtn.textContent = `Load More (${totalRemaining} remaining)`;
				
				loadMoreBtn.addEventListener('click', () => {
					this.loadMoreColors();
				});

				this.resultsContainer.appendChild(loadMoreBtn);
			} else if (this.totalCount > 0) {
				// Show "all loaded" message
				const message = document.createElement('p');
				message.className = 'ccm-all-loaded';
				message.textContent = `All ${this.totalCount} colours loaded`;
				this.resultsContainer.appendChild(message);
			}
		}

		/**
		 * Load more colors (append next batch or fetch next page)
		 */
		async loadMoreColors() {
			// Check if we have more colors in current page
			if (this.displayedCount < this.currentColors.length) {
				// Still have colors in current page, just display more
				this.renderResults(this.currentColors, true); // append = true
			} else if (this.nextPageUrl) {
				// No more colors in current page, but we have a next page - fetch it
				try {
					// Show loading state
					const loadMoreBtn = this.resultsContainer.querySelector('.ccm-load-more');
					if (loadMoreBtn) {
						loadMoreBtn.textContent = 'Loading next page...';
						loadMoreBtn.disabled = true;
					}

					// Fetch next page
					const response = await fetch(this.nextPageUrl);
					if (!response.ok) throw new Error('Failed to load next page');
					
					const data = await response.json();
					const newColors = data.colors || [];
					
					// Append new colors to existing colors
					this.currentColors.push(...newColors);
					
					// Update next page URL
					if (data.next) {
						const currentUrlObj = new URL(this.nextPageUrl);
						const baseUrlPath = currentUrlObj.origin + currentUrlObj.pathname.substring(0, currentUrlObj.pathname.lastIndexOf('/') + 1);
						this.nextPageUrl = baseUrlPath + data.next;
					} else {
						this.nextPageUrl = null; // No more pages
					}
					
					// Render the newly loaded colors
					this.renderResults(this.currentColors, true); // append = true

				} catch (error) {
					console.error('Failed to load next page', error);
					this.showError('Failed to load more colors');
				}
			}
		}

	/**
	 * Create color card element
	 */
	createColorCard(color) {
		const card = document.createElement('div');
		card.className = 'ccm-color-card';
		card.setAttribute('role', 'button');
		card.setAttribute('tabindex', '0');

		const rgbString = `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`;
		
		// Check if RGB is 0,0,0 (no preview available)
		const isNoPreview = color.rgb.r === 0 && color.rgb.g === 0 && color.rgb.b === 0;
		
		// Get category or subcategory name
		let displayName = '';
		
		// Convert IDs to numbers for comparison
		const subcategoryId = parseInt(color.subcategory_id, 10);
		const categoryId = parseInt(color.category_id, 10);
		
		// Check if categories are loaded and not empty
		if (this.categories && this.categories.length > 0) {
			if (subcategoryId && subcategoryId > 0) {
				// Try to find subcategory name
				for (const cat of this.categories) {
					if (cat.subcategories && cat.subcategories.length > 0) {
						const subcat = cat.subcategories.find(sc => parseInt(sc.id, 10) === subcategoryId);
						if (subcat) {
							displayName = subcat.name;
							break;
						}
					}
				}
			}
			
			// If no subcategory found, use category name
			if (!displayName && categoryId && categoryId > 0) {
				const category = this.categories.find(cat => parseInt(cat.id, 10) === categoryId);
				if (category) {
					displayName = category.name;
				}
			}
		}

		// Build swatch HTML - show "no preview" message if RGB is 0,0,0
		const swatchHtml = isNoPreview 
			? `<div class="ccm-color-swatch ccm-no-preview">
				<span class="ccm-no-preview-text">No preview available</span>
			</div>`
			: `<div class="ccm-color-swatch" style="background-color: ${this.escapeHtml(rgbString)};"></div>`;

		card.innerHTML = `
			${swatchHtml}
			<div class="ccm-color-info">
				<div class="ccm-color-code">${this.escapeHtml(color.code)}</div>
				<div class="ccm-color-name">${this.escapeHtml(color.name)}</div>
				<div class="ccm-color-chart">${this.escapeHtml(displayName)}</div>
				<!-- <div class="ccm-color-rgb">${this.escapeHtml(rgbString)}</div> -->	

			</div>
		`;

		// Click handler
		card.addEventListener('click', () => this.selectColor(color));

		// Keyboard accessibility
		card.addEventListener('keydown', (e) => {
			if (e.key === 'Enter' || e.key === ' ') {
				e.preventDefault();
				this.selectColor(color);
			}
		});

		return card;
	}

		/**
		 * Get color chart name (category or subcategory) for a color
		 * @param {Object} color - Color object
		 * @returns {string} Chart name or empty string
		 */
		getColorChartName(color) {
			let displayName = '';
			
			// Convert IDs to numbers for comparison
			const subcategoryId = parseInt(color.subcategory_id, 10);
			const categoryId = parseInt(color.category_id, 10);
			
			// Check if categories are loaded and not empty
			if (this.categories && this.categories.length > 0) {
				if (subcategoryId && subcategoryId > 0) {
					// Try to find subcategory name
					for (const cat of this.categories) {
						if (cat.subcategories && cat.subcategories.length > 0) {
							const subcat = cat.subcategories.find(sc => parseInt(sc.id, 10) === subcategoryId);
							if (subcat) {
								displayName = subcat.name;
								break;
							}
						}
					}
				}
				
				// If no subcategory found, use category name
				if (!displayName && categoryId && categoryId > 0) {
					const category = this.categories.find(cat => parseInt(cat.id, 10) === categoryId);
					if (category) {
						displayName = category.name;
					}
				}
			}
			
			return displayName;
		}

		/**
		 * Select a color and add to cart
		 */
		selectColor(color) {
			this.selectedColor = color;

			// Remove any existing error messages
			const existingErrors = document.querySelectorAll('.ccm-color-required-error');
			existingErrors.forEach(error => error.remove());

			// Remove existing color properties first to prevent duplicates
			this.removeColorProperties();

			// Check if this is a custom color
			const isCustom = color.id === 'custom';

			if (isCustom) {
				// Custom color: use "Colour Brand" and "Colour Name or Code"
				const brandField = document.createElement('input');
				brandField.type = 'hidden';
				brandField.name = 'properties[Colour Brand]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				brandField.value = String(color.name || '').replace(/[\r\n]+/g, ' ').trim();
				brandField.className = 'ccm-property';
				this.productForm.appendChild(brandField);

				const nameOrCodeField = document.createElement('input');
				nameOrCodeField.type = 'hidden';
				nameOrCodeField.name = 'properties[Colour Name or Code]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				nameOrCodeField.value = String(color.code || '').replace(/[\r\n]+/g, ' ').trim();
				nameOrCodeField.className = 'ccm-property';
				this.productForm.appendChild(nameOrCodeField);

				// Add colour chart field with "Custom" value
				const chartField = document.createElement('input');
				chartField.type = 'hidden';
				chartField.name = 'properties[Colour Chart]';
				chartField.value = 'Custom';
				chartField.className = 'ccm-property';
				this.productForm.appendChild(chartField);
			} else {
				// Regular color: use existing labels
				// Get color chart name
				const chartName = this.getColorChartName(color);

				// Add color to product form as line item property
				const colorField = document.createElement('input');
				colorField.type = 'hidden';
				colorField.name = 'properties[Kleur]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				colorField.value = String(color.name || '').replace(/[\r\n]+/g, ' ').trim();
				colorField.className = 'ccm-property';
				this.productForm.appendChild(colorField);

				// Add color code field for reference
				const codeField = document.createElement('input');
				codeField.type = 'hidden';
				codeField.name = 'properties[Kleurcode]';
				// Remove newlines and trim to prevent display issues in Shopify cart
				codeField.value = String(color.code || '').replace(/[\r\n]+/g, ' ').trim();
				codeField.className = 'ccm-property';
				this.productForm.appendChild(codeField);

				// Add color chart field if available
				if (chartName) {
					const chartField = document.createElement('input');
					chartField.type = 'hidden';
					chartField.name = 'properties[Kleurenwaaier]';
					// Remove newlines and trim to prevent display issues in Shopify cart
					chartField.value = String(chartName || '').replace(/[\r\n]+/g, ' ').trim();
					chartField.className = 'ccm-property';
					this.productForm.appendChild(chartField);
				}
			}

			// Show success message
			this.showSuccessMessage(color);

			// Close modal
			this.closeModal();
		}

		/**
		 * Remove existing color properties
		 */
		removeColorProperties() {
			// Remove all existing color-related inputs (both regular and custom)
			const existingColorFields = this.productForm.querySelectorAll(
				'input[name="properties[Kleur]"], ' +
				'input[name="properties[Kleurcode]"], ' +
				'input[name="properties[Kleurenwaaier]"], ' +
				'input[name="properties[Colour Brand]"], ' +
				'input[name="properties[Colour Name or Code]"]'
			);
			existingColorFields.forEach(field => field.remove());
			
			// Also remove any fields with the ccm-property class (for consistency)
			const existingProps = this.productForm.querySelectorAll('.ccm-property');
			existingProps.forEach(prop => prop.remove());
		}

		/**
		 * Setup form validation to require color selection
		 * Only validates if the selected variant has color_picker enabled
		 */
		setupFormValidation() {
			// Intercept form submission
			this.productForm.addEventListener('submit', (e) => {
				// Only validate if current variant requires color picker
				if (this.hasColorPickerVariant() && !this.hasColorSelected()) {
					e.preventDefault();
					e.stopPropagation();
					this.showColorRequiredError();
					return false;
				}
			});

			// Also intercept buy buttons (Shopify sometimes uses separate buttons)
			const buyButtons = this.productForm.querySelectorAll('button[type="submit"], button[name="add"], .product-form__cart-submit, [data-add-to-cart]');
			buyButtons.forEach(button => {
				button.addEventListener('click', (e) => {
					// Only validate if current variant requires color picker
					if (this.hasColorPickerVariant() && !this.hasColorSelected()) {
						e.preventDefault();
						e.stopPropagation();
						this.showColorRequiredError();
						return false;
					}
				});
			});
		}

		/**
		 * Check if a color has been selected
		 */
		hasColorSelected() {
			const colorField = this.productForm.querySelector('input[name="properties[Kleur]"]');
			const customBrandField = this.productForm.querySelector('input[name="properties[Colour Brand]"]');
			return (colorField && colorField.value.trim().length > 0) || 
			       (customBrandField && customBrandField.value.trim().length > 0);
		}

		/**
		 * Show error message when color is required
		 */
		showColorRequiredError() {
			// Remove existing error messages
			const existingErrors = document.querySelectorAll('.ccm-color-required-error');
			existingErrors.forEach(error => error.remove());

			// Create error message
			const errorMessage = document.createElement('div');
			errorMessage.className = 'ccm-color-required-error';
			errorMessage.innerHTML = `
				<div class="ccm-error-content">
					<strong>⚠️ Kies eerst een kleur</strong>
					<p>Selecteer een kleur voordat je het product toevoegt aan je winkelwagen.</p>
				</div>
			`;

			// Insert after the color button or before the form
			const colorButton = document.querySelector('.ccm-choose-color-btn');
			if (colorButton && colorButton.parentNode) {
				colorButton.parentNode.insertBefore(errorMessage, colorButton.nextSibling);
			} else if (this.productForm.parentNode) {
				this.productForm.parentNode.insertBefore(errorMessage, this.productForm);
			}

			// Scroll to error message
			errorMessage.scrollIntoView({ behavior: 'smooth', block: 'nearest' });

			// Remove error after 10 seconds
			setTimeout(() => {
				errorMessage.remove();
			}, 10000);
		}

		/**
		 * Show success message after color selection
		 */
	showSuccessMessage(color, onLoad = false) {
		// Remove ALL existing success messages (they're outside the form, so search document-wide)
		const existingMessages = document.querySelectorAll('.ccm-success-message');
		existingMessages.forEach(msg => msg.remove());

		// If onLoad, get color from hidden field
		if (onLoad) {
			const colorField = this.productForm.querySelector('input[name="properties[Colour]"]');
			if (!colorField || !colorField.value) return;
		}

		const message = document.createElement('div');
		message.className = 'ccm-success-message';
		
		const displayColor = color || { name: 'Selected colour' };
		const rgbString = color ? (color.rgb_string || `rgb(${color.rgb.r},${color.rgb.g},${color.rgb.b})`) : '#ccc';
		const isCustom = color && color.id === 'custom';
		
		// Check if RGB is 0,0,0 (no preview available)
		const isNoPreview = color && color.rgb && color.rgb.r === 0 && color.rgb.g === 0 && color.rgb.b === 0;

		// Green checkmark SVG (no background) - bigger size
		const checkmarkSvg = isCustom ? `
			<svg class="ccm-custom-checkmark" width="50" height="50" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
				<path d="M16.7071 5.29289C17.0976 5.68342 17.0976 6.31658 16.7071 6.70711L8.70711 14.7071C8.31658 15.0976 7.68342 15.0976 7.29289 14.7071L3.29289 10.7071C2.90237 10.3166 2.90237 9.68342 3.29289 9.29289C3.68342 8.90237 4.31658 8.90237 4.70711 9.29289L8 12.5858L15.2929 5.29289C15.6834 4.90237 16.3166 4.90237 16.7071 5.29289Z" fill="#22c55e"/>
			</svg>
		` : '';

		const customLabel = isCustom ? '<span class="ccm-custom-label">Custom Colour</span>' : '';
		
		// Build swatch HTML - show "no preview" or hide for custom, or show color
		let swatchHtml;
		if (isCustom) {
			swatchHtml = ''; // No swatch for custom colors
		} else if (isNoPreview) {
			swatchHtml = `<div class="ccm-success-swatch ccm-no-preview">
				<span class="ccm-no-preview-text">No preview</span>
			</div>`;
		} else {
			swatchHtml = `<div class="ccm-success-swatch" style="background-color: ${this.escapeHtml(rgbString)};"></div>`;
		}

		message.innerHTML = `
			<div class="ccm-success-content">
				${checkmarkSvg}
				${swatchHtml}
				<div class="ccm-success-text">
					<strong>Gekozen kleur:</strong>
					${displayColor.name}
					${customLabel}
				</div>
			</div>
		`;

		// Insert after the color button
		const colorButton = document.querySelector('.ccm-choose-color-btn');
		if (colorButton && colorButton.parentNode) {
			colorButton.parentNode.insertBefore(message, colorButton.nextSibling);
		}
	}

		/**
		 * Show loading state
		 */
		showLoading() {
			this.resultsContainer.innerHTML = '<div class="ccm-loading">Loading colours...</div>';
		}

		/**
		 * Show error message
		 */
		showError(message) {
			this.resultsContainer.innerHTML = `<div class="ccm-error">${this.escapeHtml(message)}</div>`;
		}

		/**
		 * Cache management
		 */
		getCache(key) {
			const cached = this.cache.get(key);
			if (!cached) return null;
			
			const now = Date.now();
			if (now - cached.timestamp > this.config.CACHE_DURATION) {
				this.cache.delete(key);
				return null;
			}
			
			return cached.data;
		}

		setCache(key, data) {
			this.cache.set(key, {
				data: data,
				timestamp: Date.now()
			});

			// Limit cache size
			if (this.cache.size > 50) {
				const firstKey = this.cache.keys().next().value;
				this.cache.delete(firstKey);
			}
		}

		/**
		 * Escape HTML to prevent XSS
		 */
		escapeHtml(text) {
			const div = document.createElement('div');
			div.textContent = text;
			return div.innerHTML;
		}
	}

	// Initialize when DOM is ready
	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', () => {
			new ColorPickerCDN(CONFIG);
		});
	} else {
		new ColorPickerCDN(CONFIG);
	}

})();

